import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchHeader } from "../../containers/common/commonSlice";

const PROD = "https://www.westerndigital.com";
const BETA = "https://beta.westerndigital.com";
const STAGE = "https://cloud-stage.westerndigital.com";

function baseHost() {
  const h = window.location.hostname.toLowerCase();
  console.log('host=', h);
  if (h.includes("stage") || h.includes("localhost")) return STAGE;
  if (h.includes("beta")) return BETA;
  return PROD;
}

function shouldPrefix(u) {
  if (!u) return false;
  const v = u.trim().toLowerCase();
  if (v.startsWith("http")) return false;
  if (v.startsWith("//")) return false;
  if (v.startsWith("#")) return false;
  if (v.startsWith("mailto:")) return false;
  if (v.startsWith("tel:")) return false;
  if (v.startsWith("javascript:")) return false;
  return v.startsWith("/");
}

function copyAttrs(from, to) {
  for (const { name, value } of Array.from(from.attributes || [])) {
    if (name === "src" || name === "href") continue;
    to.setAttribute(name, value);
  }
}

function ensureStyle(href) {
  if (!href) return;
  if (!document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = href;
    document.head.appendChild(l);
  }
}

function loadExternalAt(placeholder, node) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    copyAttrs(node, s);
    s.async = false;
    s.src = node.getAttribute("src");
    console.log('loadExternalAt');
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    placeholder.replaceWith(s);
  });
}

function runInlineAt(placeholder, node, code) {
  const s = document.createElement("script");
  copyAttrs(node, s);
  s.async = false;
  s.text = code;
  placeholder.replaceWith(s);
}

async function tryFetchText(url) {
  try {
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) return null;
    return await r.text();
  } catch {
    return null;
  }
}

async function loadSearchOverlay(host) {
  const candidates = [
    "/content/experience-fragments/store/common/search-overlay/master.html",
    "/content/experience-fragments/store/common/search-overlay/master",
    "/content/experience-fragments/store/nav/search/search-overlay/master.html",
    "/content/experience-fragments/store/nav/search/search-overlay/master"
  ];
  for (const p of candidates) {
    const html = await tryFetchText(host + p);
    if (html && html.length > 100) {
      const wrap = document.createElement("div");
      wrap.setAttribute("data-wd-overlay", "1");
      wrap.innerHTML = html;
      document.body.appendChild(wrap);
      return true;
    }
  }
  return false;
}

function ensureGreyBar() {
  if (!document.getElementById("greyBar")) {
    const s = document.createElement("section");
    s.id = "greyBar";
    s.style.display = "none";
    document.body.appendChild(s);
  }
}

function installWDRequestRewrite(host) {
  if (window.__wdRewrite) return;
  window.__wdRewrite = true;
  const ofetch = window.fetch;
  window.fetch = (input, init) => {
    const isReq = typeof Request !== "undefined" && input instanceof Request;
    let url = isReq ? input.url : input;
    if (typeof url === "string" && url.startsWith("/")) {
      const next = host + url;
      input = isReq ? new Request(next, input) : next;
    }
    return ofetch(input, init);
  };
}

function installWDXHRRewrite(host) {
  if (window.__wdXHRRewrite) return;
  window.__wdXHRRewrite = true;
  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, async, user, pass) {
    if (typeof url === "string" && url.startsWith("/")) url = host + url;
    return open.call(this, method, url, async, user, pass);
  };
}

function installDomReadyShimWithReplay() {
  if (window.__wdReadyShim) return;
  window.__wdReadyShim = true;

  const doc = document;
  const readyListeners = [];
  const fired = new WeakSet();

  const origDocAdd = Document.prototype.addEventListener;
  Document.prototype.addEventListener = function(type, listener, opts) {
    if (type === "DOMContentLoaded" && typeof listener === "function") {
      readyListeners.push(listener);
      return;
    }
    return origDocAdd.call(this, type, listener, opts);
  };

  const origWinAdd = Window.prototype.addEventListener;
  Window.prototype.addEventListener = function(type, listener, opts) {
    if (type === "load" && typeof listener === "function") {
      if (document.readyState === "complete") {
        setTimeout(() => listener.call(window, new Event("load")), 0);
        return;
      }
    }
    return origWinAdd.call(this, type, listener, opts);
  };

  window.__wdReplayDOMContentLoaded = () => {
    readyListeners.forEach((fn) => {
      if (fired.has(fn)) return;
      fired.add(fn);
      try { fn.call(doc, new Event("DOMContentLoaded")); } catch {}
    });
  };
}

function waitForSelector(sel, timeout = 8000) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(sel)) return resolve(document.querySelector(sel));
    const obs = new MutationObserver(() => {
      const el = document.querySelector(sel);
      if (el) { obs.disconnect(); resolve(el); }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => { obs.disconnect(); reject(new Error("timeout")); }, timeout);
  });
}

function neutralizeTabKeyFunctionality() {
  const noop = () => {};
  const tryReplace = () => {
    let done = false;
    try {
      if (typeof window.tabKeyFunctionality === "function") {
        window.__orig_tabKeyFunctionality = window.tabKeyFunctionality;
        window.tabKeyFunctionality = noop;
        done = true;
      }
    } catch {}
    const root = document.getElementById("searchSummaryView");
    if (root) {
      const v2 = root.__vue__;
      if (v2 && typeof v2.tabKeyFunctionality === "function") { v2.tabKeyFunctionality = noop; done = true; }
      const v3 = root.__vueParentComponent;
      if (v3 && v3.ctx && typeof v3.ctx.tabKeyFunctionality === "function") { v3.ctx.tabKeyFunctionality = noop; done = true; }
    }
    return done;
  };
  if (!tryReplace()) {
    const t = setInterval(() => { if (tryReplace()) clearInterval(t); }, 50);
    setTimeout(() => clearInterval(t), 8000);
  }
  document.addEventListener("keydown", (e) => {
    const wrap = document.getElementById("searchSummaryView");
    if (!wrap) return;
    const inside = wrap.contains(e.target);
    const isTab = e.key === "Tab" || e.keyCode === 9;
    if (inside && isTab) e.stopImmediatePropagation();
  }, true);
}

export default function Header() {
  const dispatch = useDispatch();
  const headerHTML = useSelector((s) => s.common.headerHTML);
  const containerRef = useRef(null);
  const ran = useRef(false);

  useEffect(() => { dispatch(fetchHeader()); }, [dispatch]);

  useEffect(() => {
    if (!headerHTML || !containerRef.current) return;
    if (ran.current) return;
    ran.current = true;

    const parser = new DOMParser();
    const doc = parser.parseFromString(headerHTML, "text/html");
    const root = doc.body || doc;
    const host = baseHost();
    // const host = process.env.REACT_APP_CORPORATE_URL;

    Array.from(root.querySelectorAll("[href]")).forEach((el) => {
      const href = el.getAttribute("href");
      if (shouldPrefix(href)) el.setAttribute("href", host + href);
      if (el.tagName.toLowerCase() === "link" && el.getAttribute("rel") === "stylesheet") {
        ensureStyle(el.getAttribute("href"));
      }
    });

    Array.from(root.querySelectorAll("[src]")).forEach((el) => {
      const src = el.getAttribute("src");
      if (shouldPrefix(src)) el.setAttribute("src", host + src);
    });

    const execQueue = [];
    let uid = 0;

    Array.from(root.querySelectorAll("script")).forEach((node) => {
      const type = (node.getAttribute("type") || "").toLowerCase();
      const isModule = type === "module";
      const isJs = type === "" || type === "text/javascript" || type === "application/javascript" || isModule;
      if (!isJs) return;
      const src = node.getAttribute("src");
      if (src && document.querySelector(`script[src="${src}"]`)) return;
      const ph = doc.createElement("span");
      const id = `xf-script-${++uid}`;
      ph.setAttribute("data-xf-script", id);
      node.parentNode.insertBefore(ph, node);
      execQueue.push({ id, kind: src ? "ext" : "inline", node, code: src ? "" : (node.textContent || "") });
      node.remove();
    });

    containerRef.current.innerHTML = root.innerHTML;

    (async () => {
      const hostUrl = baseHost();
      // const hostUrl = process.env.REACT_APP_CORPORATE_URL;
      installDomReadyShimWithReplay();
      installWDRequestRewrite(hostUrl);
      installWDXHRRewrite(hostUrl);
      await loadSearchOverlay(hostUrl);
      ensureGreyBar();

      for (const item of execQueue) {
        const ph = containerRef.current.querySelector(`[data-xf-script="${item.id}"]`);
        if (!ph) continue;
        if (item.kind === "ext") {
          await loadExternalAt(ph, item.node);
        } else {
          runInlineAt(ph, item.node, item.code);
        }
      }

      try { await waitForSelector(".btn-search-SummaryView"); } catch {}
      //neutralizeTabKeyFunctionality();
      if (typeof window.__wdReplayDOMContentLoaded === "function") window.__wdReplayDOMContentLoaded();
    })();
  }, [headerHTML]);

  return <header ref={containerRef} className="header relative w-full header-2-5" />;
}
